import { MapboxOverlay, MapboxOverlayProps } from '@deck.gl/mapbox/typed';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import bbox from '@turf/bbox';
import { ReactNode } from 'react';
import { useControl } from 'react-map-gl';
import { ViewportProps } from '../../../types/map';

export const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
export const MAPBOX_STYLE_TOKEN = import.meta.env.VITE_MAPBOX_STYLE_TOKEN;

export interface IMapProps {
  className?: string;
  popupContent: ReactNode | null;
  popupCoordinates: Array<number> | null;
  layers: Array<any>;
  onPopupCoordinatesChanged?: (
    object: any,
    layerId: string,
    skipDuplicateCheck?: boolean
  ) => void;
  onClosePopup?: () => void;
  onClick?: (coordinates: number[]) => void;
  onMoveMouse?: (coordinates: [number, number]) => void;
  addMeasurementPoint: (point: [number, number]) => void;
}

export function fitBounds(
  feature: GeoJSON.GeoJsonObject,
  vp: ViewportProps,
  padding = 40
) {
  const [minLng, minLat, maxLng, maxLat] = bbox(feature);
  // construct a viewport instance from the current state
  const viewport = new WebMercatorViewport(vp);
  const { longitude, latitude, zoom } = viewport.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    {
      padding,
    }
  );
  return { longitude, latitude, zoom };
}

export function fitExtent(
  extentDefinition: string,
  vp: ViewportProps,
  padding = 40
) {
  const [minLng, minLat, maxLng, maxLat] = extentDefinition.split(',');
  // construct a viewport instance from the current state
  const viewport = new WebMercatorViewport(vp);
  const { longitude, latitude, zoom } = viewport.fitBounds(
    [
      [Number(minLng), Number(minLat)],
      [Number(maxLng), Number(maxLat)],
    ],
    {
      padding,
    }
  );
  return { longitude, latitude, zoom };
}

export function getBounds(vp: ViewportProps) {
  // construct a viewport instance from the current state
  const viewport = new WebMercatorViewport(vp);
  const bounds = (viewport as any).getBounds();
  return bounds;
}
export interface IMapCenter {
  latitude: number;
  longitude: number;
  zoom: number;
}
export const MapCenter = { latitude: 31, longitude: 0, zoom: 1.5 };

export const ML_DATASET_MESH = 'ML_DATASET_MESH';

export const ML_ICON_POINT = 'ML_ICON_POINT';

/* normalize co-ordinates that lie outside of the normal ranges.
  longitude wrapping simply requires adding +- 360 to the value until it comes
  in to range.
  for the latitude values we need to flip the longitude whenever the latitude
  crosses a pole. */
export const wrapCoordinates = (coordinates: number[]) => {
  const [longitude, latitude] = coordinates;
  const point = { latitude: latitude, longitude: longitude };
  const quadrant = Math.floor(Math.abs(latitude) / 90) % 4;
  const pole = latitude > 0 ? 90 : -90;
  const offset = latitude % 90;

  switch (quadrant) {
    case 0:
      point.latitude = offset;
      break;
    case 1:
      point.latitude = pole - offset;
      point.longitude += 180;
      break;
    case 2:
      point.latitude = -offset;
      point.longitude += 180;
      break;
    case 3:
      point.latitude = -pole + offset;
      break;
  }

  if (point.longitude > 180 || point.longitude < -180) {
    point.longitude -= Math.floor((point.longitude + 180) / 360) * 360;
  }

  return [point.longitude, point.latitude];
};

export function groupPointsByPosition(points: any[]) {
  const groups = new Map();

  points.forEach((point) => {
    const positionKey = point.position.slice(0, 2).join(',');
    if (!groups.has(positionKey)) {
      groups.set(positionKey, []);
    }
    groups.get(positionKey).push(point);
  });

  return Array.from(groups.values());
}

export function createSemiCircularOffset(
  index: number,
  total: number,
  radius: number = 0.05
) {
  const angle = -Math.PI / 2 + (index / (total - 1)) * 2 * Math.PI;
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;

  return [x, y];
}

export function createExpandedPoints(points: any[]) {
  const groupedPoints = groupPointsByPosition(points);

  const expandedPoints: any[] = [];
  const lines: any[] = [];

  groupedPoints.forEach((group) => {
    if (group.length === 1) return;

    const centerPoint = group[0].position;

    group.forEach((point: { position: any[] }, index: number) => {
      const [offsetX, offsetY] = createSemiCircularOffset(
        index,
        group.length,
        0.5
      );

      const newPosition = [
        point.position[0] + offsetX,
        point.position[1] + offsetY,
        point.position[2] || 0,
      ];

      expandedPoints.push({
        ...point,
        position: newPosition,
        originalPosition: point.position,
        isExpanded: true,
      });

      lines.push({
        sourcePosition: centerPoint,
        targetPosition: newPosition,
        groupId: `group-${centerPoint.join('-')}`,
      });
    });
  });

  return { expandedPoints, lines };
}

export const INITIAL_VIEW_STATE: ViewportProps = {
  height: 400,
  width: 400,
  latitude: 31,
  longitude: 0,
  zoom: 1.5,
  transitionDuration: 1000,
  pitch: 0,
  bearing: 0,
};

export function DeckGLOverlay(
  props: MapboxOverlayProps & {
    interleaved?: boolean;
  }
) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}
